import { Directive, OnInit, ViewChild, OnDestroy, Component, ChangeDetectorRef } from '@angular/core';
import { DateRangeService } from '@shared/date-range-presets/date-range.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDrawer, MatDrawerToggleResult } from '@angular/material/sidenav';
import { auditTime, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import {
  FilterService,
  FilterSetupCalendarType,
  FilterSetupMatauranga,
  FilterSetupMonitoring,
  FilterSetupObservation,
  FilterSetupType,
  FilterSetupSubscribe,
} from '@app/data/services/filterService';
import { DrawerService, IDrawerDataChange } from '@app/data/services/drawerService';
import { EChartsOption } from 'echarts';
import { Site, SitesService } from '@app/data/services/sitesService';
import { LocationsService, SelectedLocationService } from '@app/data/services/locationServices';
import { EnvironmentalDataSourcesService } from '@app/data/services/environmentalDataSourceServices';
import { EnvironmentalDataSourceDashGroupsService } from '@app/data/services/environmentalDataSourceDashGroupsServices';
import { UnsubscribeManager } from '@app/data/class/unsubscribe-manager.class';
import { ClientService } from '@app/client/client.service';
import { ClientConstantType } from '@app/client/client.constant';
import { MapService } from '@app/data/services/map.service';
import { DateRangePresetsComponent } from '@app/@shared/date-range-presets/date-range-presets.component';
import moment from 'moment';
import { MapLayersApi } from '@app/data/services/types/MapLayersApi';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import { ChartService } from '@app/data/services/chartService';
import { extractUniqueGroupTags } from '../../pages/mapview/utils/mapview-utils';
import {
  ContributingOrganisation,
  ContributingProject,
  SharedObservationFiltersService,
} from '@app/data/services/sharedObservationFiltersService';

/**
 * Option interface for filter drawer layout component
 *
 * key: The unique identifier for the option, typically used as the value in form controls.
 * value: The display text for the option, shown to users in the UI.
 * domain: An optional property that can hold additional metadata about the option, such as categorization or grouping information.
 */
interface FilterDrawerLayoutOption<T = string> {
  key: T;
  value: string;
  domain?: string;
}

interface FilterDrawerReorderableLayoutOption<T = string> {
  key: T;
  value: string;
  domain?: string;
  defaultZIndex?: number;
  minZIndex?: number;
  maxZIndex?: number;
  allowReordering?: boolean;
  siteName?: string;
}

export type FilterDrawerLayoutOptions<T = string> = FilterDrawerLayoutOption<T>[];
export type FilterDrawerReorderableLayoutOptions<T = string> = FilterDrawerReorderableLayoutOption<T>[];
export type FilterDrawerLayoutFilterTypes =
  | 'type'
  | 'domain'
  | 'timescale'
  | 'textfilter'
  | 'csvDownload'
  | 'surfaceSites'
  | 'mapFeatures'
  | 'baseMap'
  | 'dataLayers'
  | 'matauranga'
  | 'monitoring'
  | 'observations'
  | 'calendarView'
  | 'locationFilter'
  | 'environmentalDataSourcesFilter'
  | 'environmentalDataSourcesDashGroupsFilter'
  | 'contributingOrganisationsFilter'
  | 'contributingProjectsFilter';

@Directive()
export abstract class FilterDrawerLayoutComponentBase extends UnsubscribeManager implements OnInit, OnDestroy {
  @ViewChild('drawerView') protected drawerView?: MatDrawer;
  @ViewChild('picker') dateRangePicker!: MatDateRangePicker<Date>;
  DateRangePresetsComponent = DateRangePresetsComponent;

  type: FilterSetupType = 'taiao';
  selectedLocation: string = 'all';
  selectedEnvironmentalDataSources: string[] = ['all'];
  selectedEnvironmentalDataSourceDashGroups: string[] = ['all'];
  selectedContributingOrganisations: string[] = ['all'];
  selectedContributingProjectListItems: string[] = ['all'];

  filterForm: FormGroup = {} as FormGroup;
  typeOptions: FilterDrawerLayoutOptions<FilterSetupType> = this.compileOptions<FilterSetupType>('type', [
    { key: 'taiao', value: 'Tiro Whānui - Overview' },
    { key: 'maramataka', value: 'Tūhono' },
    { key: 'mapview', value: 'Mapi – Map View' },
  ]);
  defaultTypeOptions: FilterDrawerLayoutOptions<FilterSetupType> = this.compileOptions<FilterSetupType>('type', [
    { key: 'taiao', value: 'StreamDASH' },
    { key: 'maramataka', value: 'Tūhono' },
    { key: 'mapview', value: 'StreamMAP' },
  ]);

  defaultMapFeaturesOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>('mapFeatures', [
    { key: 'monitor', value: 'Monitoring Stations' },
    { key: 'observation', value: 'Observations' },
  ]);

  mapFeaturesOptions: FilterDrawerReorderableLayoutOptions<string> = this.compileOptions<string>('mapFeatures', [
    { key: 'monitor', value: 'Monitoring Stations' },
    { key: 'observation', value: 'Observations' },
  ]);

  mapGroupsOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>('mapFeatures', []);

  locationFilterOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>('locationFilter', [
    { key: 'all', value: 'All' },
  ]);

  environmentalDataSourcesOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>(
    'environmentalDataSourcesFilter',
    [{ key: 'all', value: 'All' }]
  );

  environmentalDataSourceDashGroupsOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>(
    'environmentalDataSourcesDashGroupsFilter',
    [{ key: 'all', value: 'All' }]
  );

  contributingOrganisationsOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>(
    'contributingOrganisationsFilter',
    [{ key: 'all', value: 'All' }]
  );

  contributingProjectListItemOptions: FilterDrawerLayoutOptions<string> = this.compileOptions<string>(
    'contributingProjectsFilter',
    [{ key: 'all', value: 'All' }]
  );

  mataurangaOptions: FilterDrawerLayoutOptions<FilterSetupMatauranga> = this.compileOptions<FilterSetupMatauranga>(
    'matauranga',
    [
      { key: 'hau', value: 'Hau' },
      { key: 'wai', value: 'Wai' },
      { key: 'moana', value: 'Moana' },
      { key: 'whenua', value: 'Whenua' },
    ]
  );

  monitoringOptions: FilterDrawerLayoutOptions<FilterSetupMonitoring> =
    this.clientService.getClient() === ClientConstantType.wastemanagement
      ? this.compileOptions<FilterSetupMonitoring>('monitoring', [
          { key: 'hau', value: 'Hau' },
          { key: 'wai', value: 'Wai' },
          { key: 'moana', value: 'Moana' },
          { key: 'whenua', value: 'Whenua' },
          { key: 'waste', value: 'Waste Management' },
        ])
      : this.compileOptions<FilterSetupMonitoring>('monitoring', [
          { key: 'hau', value: 'Hau' },
          { key: 'wai', value: 'Wai' },
          { key: 'moana', value: 'Moana' },
          { key: 'whenua', value: 'Whenua' },
        ]);

  observationOptions: FilterDrawerLayoutOptions<FilterSetupObservation> = this.compileOptions<FilterSetupObservation>(
    'observations',
    [
      { key: 'text', value: 'Text' },
      { key: 'multimedia', value: 'Multimedia' },
    ]
  );

  calendarTypeOptions: FilterDrawerLayoutOptions<FilterSetupCalendarType> =
    this.compileOptions<FilterSetupCalendarType>('calendarView', [
      { key: 'day', value: 'Day' },
      { key: 'week', value: 'Week' },
      { key: 'month', value: 'Month' },
    ]);

  /**
   * Below options are not compatible with `compileOptions`, so ... yeah
   */
  calendarTypeYearOptions: number[] = new Array(4)
    .fill(moment().year())
    .reduce((accumulator, val, index) => [...accumulator, val - index], [])
    .reverse();
  calendarTypeMonthOptions: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  calendarTypeWeekOptions: number[] = new Array(5).fill('').map((_v, k) => k + 1);
  calendarTypeDayOptions: number[] = new Array(31).fill('').map((_v, k) => k + 1);

  filtersInUse: Record<FilterDrawerLayoutFilterTypes, FilterSetupType[]> = {
    type: ['taiao', 'sitehealth', 'mapview', 'maramataka'],
    domain: ['taiao', 'sitehealth'],
    timescale: ['taiao', 'mapview'],
    textfilter: ['mapview'],
    csvDownload: ['mapview'],
    surfaceSites: ['mapview'],
    mapFeatures: ['mapview'],
    baseMap: ['mapview'],
    dataLayers: ['mapview'],
    matauranga: ['maramataka'],
    monitoring: ['maramataka'],
    observations: ['maramataka'],
    calendarView: ['maramataka'],
    locationFilter: ['taiao'],
    environmentalDataSourcesFilter: ['taiao'],
    environmentalDataSourcesDashGroupsFilter: ['taiao'],
    contributingOrganisationsFilter: ['taiao'],
    contributingProjectsFilter: ['taiao'],
  };

  events: any[] = [];
  selectedEventId: string | null = null;
  selectedEventIndex: number = 0;
  selectedEvent: any | null = null;
  drawerGranularity?: string | null = null;

  chart: EChartsOption = {};
  contentClass: any = {};
  drawerIsOpen: boolean = false;
  panelOpenState: boolean = false;
  client: ClientConstantType = this.clientService.getClient();
  site: Site | null = null;
  // controls if the stream logo is shown at the top.
  hasStreamBranding = true;

  // helps control the height of the filter panel.
  hasLogo = true;
  brandPadding = true;
  filterBrandFile = '';
  myFilterService: FilterService;
  /**
   * When true, subclasses should avoid persisting mapFeatures to localStorage
   * because a programmatic (API) update is in progress.
   */
  protected suppressLocalPersistence: boolean = false;
  siteSubscription: Subscription | null = null;
  locationsSubscription: Subscription | null = null;
  selectedLocationSubscription: Subscription | null = null;
  environmentalDataSourceSubscription: Subscription | null = null;
  environmentalDataSourceDashGroupsSubscription: Subscription | null = null;
  sharedObservationOrganisationsSubscription: Subscription | null = null;
  sharedObservationProjectsSubscription: Subscription | null = null;

  /**
   * Subscription for form valueChanges to allow safe unsubscribe when reinitializing.
   */
  private valueChangesSub: Subscription | null = null;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private filterService: FilterService,
    private drawerService: DrawerService,
    private clientService: ClientService,
    private siteService: SitesService,
    private locationsService: LocationsService,
    private selectedLocationService: SelectedLocationService,
    private environmentalDataSourcesService: EnvironmentalDataSourcesService,
    private environmentalDataSourceDashGroupsService: EnvironmentalDataSourceDashGroupsService,
    protected mapService: MapService,
    protected chartService: ChartService,
    private sharedObservationFiltersService: SharedObservationFiltersService,
    private dateRangeService: DateRangeService,
    protected cdr: ChangeDetectorRef
  ) {
    super();
    this.myFilterService = filterService;

    // Preserve any existing mapGroups selection across form re-initializations
    const currentMapGroups = filterService.getFilters().mapGroups;

    // Check api for additional map feature options
    this.siteSubscription = this.siteService
      .getSelectedSite()
      .pipe(
        switchMap((site: Site | null) => {
          this.site = site;
          // Only close drawer if we're not in the process of loading a specific observation
          const isLoadingSpecificObservation = this.isLoadingObservationDeepLink();
          if (!isLoadingSpecificObservation) {
            this.drawerView?.close();
          }
          if (site) {
            return this.mapService.getMapLayers(site.id);
          }
          return of([]);
        })
      )
      .subscribe({
        next: (layers) => {
          const validLayers = (layers || []).filter((l) => !!l);
          if (validLayers.length > 0) {
            this.populateMapFeaturesOptionsFromApi(validLayers);
            this.initFilterForm();
          } else {
            this.initFilterForm();
          }
        },
        error: (err) => {
          console.error('Error fetching site or layers', err);
        },
      });

    // Check api for additional map group options
    this.siteSubscription = this.siteService
      .getSelectedSite()
      .pipe(
        switchMap((site: Site | null) => {
          this.site = site;
          // Only close drawer if we're not in the process of loading a specific observation
          // to avoid race conditions with observation loading
          const isLoadingSpecificObservation = this.isLoadingObservationDeepLink();
          if (!isLoadingSpecificObservation) {
            this.drawerView?.close();
          }
          if (site) {
            return this.mapService.getMapEnvironmentDataSources(site.id);
          }
          return of([]);
        })
      )
      .subscribe({
        next: (environmentalDataSources) => {
          this.populateMapGroupsOptionsFromApi(environmentalDataSources);
          this.filterService.setFilters({ mapGroups: currentMapGroups });
          this.initFilterForm();
        },
        error: (err) => {
          console.error('Error fetching site or environmental data sources', err);
        },
      });

    this.locationsSubscription = this.locationsService.getLocations().subscribe({
      next: (locations) => {
        if (locations) {
          let transformedLocations = locations
            .map((location) => ({
              key: location.id,
              value: location.name,
            }))
            .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));

          // Update the filters
          this.locationFilterOptions = this.compileOptions<string>('locationFilter', [
            ...transformedLocations.slice(0, 0),
            { key: 'all', value: 'All' }, // Add the "All" option
            ...transformedLocations.slice(0),
          ]);
        }
      },
      error: (err) => {
        console.error('Error fetching locations', err);
      },
    });

    this.environmentalDataSourceSubscription = this.environmentalDataSourcesService
      .getEnvironmentalDataSources()
      .subscribe({
        next: (environmentalDataSources) => {
          let transformedEnvironmentalDataSources: string | any[] = [];
          if (environmentalDataSources) {
            transformedEnvironmentalDataSources = environmentalDataSources
              .map((dataSource) => ({
                key: dataSource.id,
                value: dataSource.human_readable_name,
              }))
              .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
          }
          // Update the filters
          this.environmentalDataSourcesOptions = this.compileOptions<string>('environmentalDataSourcesFilter', [
            ...transformedEnvironmentalDataSources.slice(0, 0),
            { key: 'all', value: 'All' }, // Add the "All" option
            ...transformedEnvironmentalDataSources.slice(0),
          ]);
          this.selectedEnvironmentalDataSources = ['all'];
          this.filterService.setFilters({ selectedEnvironmentalDataSources: this.selectedEnvironmentalDataSources });
          this.initFilterForm();
        },
        error: (err) => {
          console.error('Error fetching environmental data sources', err);
        },
      });

    this.environmentalDataSourceDashGroupsSubscription = this.environmentalDataSourceDashGroupsService
      .getEnvironmentalDataSourceDashGroups()
      .subscribe({
        next: (dashGroups) => {
          let transformedDashGroups: FilterDrawerLayoutOptions<string> = [];
          if (dashGroups) {
            transformedDashGroups = dashGroups
              .map((group) => ({
                key: group,
                value: group,
              }))
              .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
          }
          // Update the filters
          this.environmentalDataSourceDashGroupsOptions = this.compileOptions<string>(
            'environmentalDataSourcesDashGroupsFilter',
            [
              ...transformedDashGroups.slice(0, 0),
              { key: 'all', value: 'All' }, // Add the "All" option
              ...transformedDashGroups.slice(0),
            ]
          );
          this.selectedEnvironmentalDataSourceDashGroups = ['all'];
          this.filterService.setFilters({
            selectedEnvironmentalDataSourceDashGroups: this.selectedEnvironmentalDataSourceDashGroups,
          });
          this.initFilterForm();
        },
        error: (err) => {
          console.error('Error fetching environmental data source dash groups', err);
        },
      });

    this.sharedObservationOrganisationsSubscription = this.siteService
      .getSelectedSite()
      .pipe(
        switchMap((site: Site | null) => {
          if (site) {
            return this.sharedObservationFiltersService.getContributingOrganisations();
          }
          return of([]);
        })
      )
      .subscribe({
        next: (organisations) => {
          this.populateContributingOrganisationsOptionsFromApi(organisations || []);
          this.selectedContributingOrganisations = ['all'];
          this.filterService.setFilters({
            selectedContributingOrganisations: this.selectedContributingOrganisations,
            selectedContributingProjectListItems: ['all'],
          });
          this.refreshContributingProjectOptions(this.selectedContributingOrganisations);
          this.initFilterForm();
        },
        error: (err) => {
          console.error('Error fetching contributing organisations', err);
        },
      });

    // Now we need to handle selected location changes
    this.selectedLocationSubscription = this.selectedLocationService.getSelectedLocation().subscribe({
      next: (location) => {
        if (location) {
          this.selectedLocation = location.id;
        } else {
          this.selectedLocation = 'all';
        }

        // Update the filters
        this.filterService.setFilters({ selectedLocation: this.selectedLocation });
        this.initFilterForm();
      },
      error: (err) => {
        console.error('Error fetching selected location', err);
      },
    });

    this.filterService.subscribe(this.handleSelectedLocationUpdate.bind(this), ['selectedLocation']);
    this.filterService.subscribe(this.handleSelectedEnvironmentalDataSourcesUpdate.bind(this), [
      'selectedEnvironmentalDataSources',
    ]);
    this.filterService.subscribe(this.handleSelectedEnvironmentalDataSourceDashGroupsUpdate.bind(this), [
      'selectedEnvironmentalDataSourceDashGroups',
    ]);
    this.filterService.subscribe(this.handleSelectedContributingOrganisationsUpdate.bind(this), [
      'selectedContributingOrganisations',
    ]);
    this.filterService.subscribe(this.handleSelectedContributingProjectListItemsUpdate.bind(this), [
      'selectedContributingProjectListItems',
    ]);

    this.subscribe(this.drawerService, this.setDrawerData.bind(this));

    this.type = this.getTypeFromRoute();

    /**
     * Set the type when changes pages
     */
    this.subscribeOnObservable(this.router.events, () => {
      this.type = this.getTypeFromRoute();

      this.filterForm.controls.type.setValue(this.type);
      this.contentClass = { [this.type]: true };
      this.getFilterBrandFile();

      // Only close drawer and reset events if we're not loading a specific observation
      // to avoid race conditions with observation loading
      const isLoadingSpecificObservation = this.isLoadingObservationDeepLink();
      if (!isLoadingSpecificObservation && this.drawerView?.opened) {
        this.drawerView?.close();
      }
      if (!isLoadingSpecificObservation) {
        this.events = [];
        this.selectedEvent = null;
        this.selectedEventId = null;
        this.selectedEventIndex = 0;
      }
    });

    this.contentClass = { [this.type]: true };

    this.initFilterForm();
  }

  ngOnInit(): void {
    console.log('FilterDrawerLayoutComponentBase ngOnInit');
    console.log(this.hasStreamBranding);
    console.log(this.hasLogo);

    this.getFilterBrandFile();
    this.initFilterForm();

    this.dateRangeService.getRangeObservable().subscribe({
      next: (range) => {
        this.filterForm.controls.fromDate.setValue(range.start);
        this.filterForm.controls.toDate.setValue(range.end);
        this.dateRangePicker.close();
      },
    });
  }

  private isLoadingObservationDeepLink(): boolean {
    return (
      window.location.pathname.includes('/mapview/observation/') ||
      window.location.pathname.includes('/mapview-old/observation/')
    );
  }

  private isLegacyMapviewRoute(): boolean {
    return this.router.url.includes('mapview-old');
  }

  public getScrollbarContainerClass(): string {
    if (this.hasLogo && this.hasStreamBranding) {
      return 'scrollableContainerWithBranding';
    }
    if (!this.hasLogo && this.hasStreamBranding) {
      return 'scrollableContainerWithBrandingOnly';
    }
    return 'scrollableContainer';
  }

  ngOnDestroy(): void {
    if (this.siteSubscription) {
      this.siteSubscription.unsubscribe();
    }

    if (this.locationsSubscription) {
      this.locationsSubscription.unsubscribe();
    }

    if (this.selectedLocationSubscription) {
      this.selectedLocationSubscription.unsubscribe();
    }

    if (this.sharedObservationOrganisationsSubscription) {
      this.sharedObservationOrganisationsSubscription.unsubscribe();
    }

    if (this.sharedObservationProjectsSubscription) {
      this.sharedObservationProjectsSubscription.unsubscribe();
    }
  }

  private setSelectedLocation(locationId: string) {
    this.selectedLocation = locationId;
    this.selectedLocationService.setSelectedLocationById(locationId);
  }

  private handleSelectedLocationUpdate({ filters, previousFilters }: FilterSetupSubscribe): void {
    if (
      filters.selectedLocation !== previousFilters.selectedLocation &&
      filters.selectedLocation !== this.selectedLocation
    ) {
      this.setSelectedLocation(filters.selectedLocation);
    }
  }

  private handleSelectedEnvironmentalDataSourcesUpdate({ filters, previousFilters }: FilterSetupSubscribe): void {
    if (
      filters.selectedEnvironmentalDataSources !== previousFilters.selectedEnvironmentalDataSources &&
      filters.selectedEnvironmentalDataSources !== this.selectedEnvironmentalDataSources
    ) {
      if (this.selectedEnvironmentalDataSources[0] !== 'all' && filters.selectedEnvironmentalDataSources[0] === 'all') {
        this.selectedEnvironmentalDataSources = [filters.selectedEnvironmentalDataSources[0]];
        this.filterService.setFilters({ selectedEnvironmentalDataSources: this.selectedEnvironmentalDataSources });
      } else if (
        filters.selectedEnvironmentalDataSources.length > 1 &&
        filters.selectedEnvironmentalDataSources[0] === 'all'
      ) {
        this.selectedEnvironmentalDataSources = filters.selectedEnvironmentalDataSources.slice(1);
        this.filterService.setFilters({ selectedEnvironmentalDataSources: this.selectedEnvironmentalDataSources });
      } else {
        this.selectedEnvironmentalDataSources = filters.selectedEnvironmentalDataSources;
      }
      this.initFilterForm();
    }
  }

  private handleSelectedEnvironmentalDataSourceDashGroupsUpdate({
    filters,
    previousFilters,
  }: FilterSetupSubscribe): void {
    if (
      filters.selectedEnvironmentalDataSourceDashGroups !== previousFilters.selectedEnvironmentalDataSourceDashGroups &&
      filters.selectedEnvironmentalDataSourceDashGroups !== this.selectedEnvironmentalDataSourceDashGroups
    ) {
      if (
        this.selectedEnvironmentalDataSourceDashGroups[0] !== 'all' &&
        filters.selectedEnvironmentalDataSourceDashGroups[0] === 'all'
      ) {
        this.selectedEnvironmentalDataSourceDashGroups = [filters.selectedEnvironmentalDataSourceDashGroups[0]];
        this.filterService.setFilters({
          selectedEnvironmentalDataSourceDashGroups: this.selectedEnvironmentalDataSourceDashGroups,
        });
      } else if (
        filters.selectedEnvironmentalDataSourceDashGroups.length > 1 &&
        filters.selectedEnvironmentalDataSourceDashGroups[0] === 'all'
      ) {
        this.selectedEnvironmentalDataSourceDashGroups = filters.selectedEnvironmentalDataSourceDashGroups.slice(1);
        this.filterService.setFilters({
          selectedEnvironmentalDataSourceDashGroups: this.selectedEnvironmentalDataSourceDashGroups,
        });
      } else {
        this.selectedEnvironmentalDataSourceDashGroups = filters.selectedEnvironmentalDataSourceDashGroups;
      }
      this.initFilterForm();
    }
  }

  private handleSelectedContributingOrganisationsUpdate({ filters, previousFilters }: FilterSetupSubscribe): void {
    if (
      filters.selectedContributingOrganisations !== previousFilters.selectedContributingOrganisations &&
      filters.selectedContributingOrganisations !== this.selectedContributingOrganisations
    ) {
      this.selectedContributingOrganisations = this.normaliseAllSelection(filters.selectedContributingOrganisations);
      this.filterService.setFilters({ selectedContributingOrganisations: this.selectedContributingOrganisations });
      this.refreshContributingProjectOptions(this.selectedContributingOrganisations);
      this.initFilterForm();
    }
  }

  private handleSelectedContributingProjectListItemsUpdate({ filters, previousFilters }: FilterSetupSubscribe): void {
    if (
      filters.selectedContributingProjectListItems !== previousFilters.selectedContributingProjectListItems &&
      filters.selectedContributingProjectListItems !== this.selectedContributingProjectListItems
    ) {
      this.selectedContributingProjectListItems = this.normaliseAllSelection(
        filters.selectedContributingProjectListItems
      );
      this.filterService.setFilters({
        selectedContributingProjectListItems: this.selectedContributingProjectListItems,
      });
      this.initFilterForm();
    }
  }

  private normaliseAllSelection(value: string[] | null | undefined): string[] {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    if (!values.length || values.includes('all')) {
      return ['all'];
    }
    return values;
  }

  getFilterBrandFile(): void {
    if (!this.hasStreamBranding) {
      return;
    }
    const route = this.router.url.split('/')[1];
    const routeImgs: { [route: string]: string } = {
      mapview: 'assets/clients/paepae/stream-map-logo.jpg',
      'mapview-old': 'assets/clients/paepae/stream-map-logo.jpg',
      maramatakacalendar: 'assets/clients/paepae/tuhono-logo.jpg',
      taiao: 'assets/clients/paepae/stream-dash-logo.jpg',
      default: 'assets/the-stream-logo.png',
    };
    this.filterBrandFile = routeImgs[route] ?? routeImgs.default;
  }

  private getTypeFromRoute(): FilterSetupType {
    let type: FilterSetupType = 'taiao';

    if (this.router.url.includes('maramatakacalendar')) {
      type = 'maramataka';
    } else if (this.router.url.includes('sitehealth')) {
      type = 'sitehealth';
    } else if (this.router.url.includes('mapview')) {
      type = 'mapview';
    }

    return type;
  }

  private getRouteFromType(): string {
    let route: string = '/taiao';

    if (this.type === 'maramataka') {
      route = '/maramatakacalendar';
    } else if (this.type === 'sitehealth') {
      route = '/sitehealth';
    } else if (this.type === 'mapview') {
      route = this.isLegacyMapviewRoute() ? '/mapview-old' : '/mapview';
    }

    return route;
  }

  protected abstract getCheckboxFields(): string[];
  protected abstract getMultipleSelectFields(): string[];

  /**
   * Fields that should be excluded from the base-class automatic setFilters flow and
   * instead applied manually by subclasses via their own UI (e.g. an "Apply" button).
   * Subclasses can override to return an array of field names to exclude.
   */
  protected getManualApplyFields(): string[] {
    return [];
  }

  private buildCheckboxGroup(options: FilterDrawerLayoutOptions, selectedOptions: string[] | null = null): FormGroup {
    return this.fb.group(
      options.reduce((acc, x) => ({ ...acc, [x.key]: (selectedOptions || []).includes(x.key) }), {})
    );
  }

  /**
   * Compile (manipulate) options
   *
   * Method is a hook function to manipulate any FilterDrawerLayoutOptions options
   *
   * @access protected
   * @param {FilterDrawerLayoutFilterTypes} type The options/filter type
   * @param {FilterDrawerLayoutOptions<T>} baseOptions The base options to work off
   * @returns {FilterDrawerLayoutOptions<T>} The filtered/manipulated/compiled options
   */
  protected compileOptions<T>(
    _type: FilterDrawerLayoutFilterTypes,
    baseOptions: FilterDrawerLayoutOptions<T>
  ): FilterDrawerLayoutOptions<T> {
    return baseOptions;
  }

  /**
   * Hook called after the map feature options are rebuilt from the API response.
   *
   * Subclasses can override to recompute any derived display state (for example
   * the ordered options rendered in the drawer) so the UI reflects the new
   * options immediately, rather than waiting for an unrelated filter change.
   */
  protected onMapFeaturesOptionsUpdated(): void {}

  /**
   * Hook called just before the FilterService setFilters is invoked from the form valueChanges
   * Allows subclasses to intercept and persist certain filter values (for example, mapFeatures)
   * synchronously so that enforcement logic in subclasses can rely on persisted state.
   * Default implementation is a no-op, but subclasses may override.
   */
  protected onLocalMapFeaturesChange(_mapFeatures: string[] | null): void {}

  canDisplaySection(section: FilterDrawerLayoutFilterTypes): boolean {
    return this.filtersInUse[section].includes(this.type);
  }

  getCalendarType(): FilterSetupCalendarType {
    return this.filterForm.value.calendarType;
  }

  handleTypeChange(): void {
    this.type = this.filterForm.controls.type.value;

    this.router.navigate([this.getRouteFromType()]);
  }

  setDrawerData(data: IDrawerDataChange): void {
    this.events = data.data;
    this.drawerGranularity = data.granularity;
    // This is a gross, to be redone.
    if (this.drawerGranularity === 'day') this.drawerGranularity = 'dai';

    if (this.events.length === 1) {
      this.setSelectedEvent(this.events[0]);
    } else {
      this.unsetSelectedEvent();
    }

    // Ensure drawer opens with a slight delay to handle any pending close operations
    if (!this.drawerView?.opened) {
      setTimeout(() => {
        if (this.drawerView && !this.drawerView.opened) {
          this.drawerView.open();
        }
      }, 10);
    }
  }

  setSelectedEvent(event: any): void {
    this.selectedEventIndex = this.events.findIndex((x) => event.id === x.id);

    if (this.selectedEventIndex < 0) {
      throw new Error('Cannot find selected event');
    }

    this.selectedEvent = event;
    this.selectedEventId = event.id;
  }

  unsetSelectedEvent(): void {
    this.selectedEventId = null;
    this.selectedEvent = null;
  }

  setNextEvent(): void {
    const index = this.events.findIndex((x) => x.id === this.selectedEventId);

    if (index === -1 || index >= this.events.length) {
      return;
    }

    this.setSelectedEvent(this.events[index + 1]);
  }

  setPreviousEvent(): void {
    const index = this.events.findIndex((x) => x.id === this.selectedEventId);

    if (index <= 0) {
      return;
    }

    this.setSelectedEvent(this.events[index - 1]);
  }

  toggleDrawer(): Promise<MatDrawerToggleResult> {
    return (this.drawerView as MatDrawer).toggle();
  }

  gotoDashboard(): void {
    this.drawerView?.close();
    this.router.navigate(['/taiao']);
  }

  getDataPointName(dataPoint: any): string {
    if (dataPoint.entity && dataPoint.entity.name && dataPoint.analyte && dataPoint.analyte.long_name) {
      return `${dataPoint.entity.name} (${dataPoint.analyte.long_name})`;
    } else if (dataPoint.analyte && dataPoint.analyte.long_name) {
      return dataPoint.analyte.long_name;
    } else {
      return 'Unknown';
    }
  }

  getLatLngDisplayString(lat: any, lng: any) {
    /**
     * Previous code. Have a look when there is time
     */
    let tmpLat = lat;
    let tmpLng = lng;

    let retString = '' + Math.floor(Math.abs(lat) % 100) + '°';
    tmpLat = (Math.abs(lat) - Math.floor(Math.abs(lat) % 100)) * 100;

    retString += ('' + Math.floor(Math.abs(tmpLat) % 100)).padStart(2, '0') + "'";
    tmpLat = (((tmpLat / 100) * 100) / 100) * 100;
    tmpLat = (Math.abs(tmpLat) - Math.floor(Math.abs(tmpLat) % 100)) * 100;

    retString += Math.floor(Math.abs(tmpLat) % 100);

    tmpLat = (Math.abs(tmpLat) - Math.floor(Math.abs(tmpLat) % 100)) * 100;
    tmpLat = Math.round((((tmpLat / 100) * 100) / 100) * 10);

    retString += '.' + tmpLat + '"';

    if (lat > 0) {
      retString += 'N ';
    } else if (lat < 0) {
      retString += 'S ';
    } else {
      retString += ' ';
    }

    retString += Math.floor(Math.abs(lng) % 1000) + '°';
    tmpLng = (Math.abs(lng) - Math.floor(Math.abs(lng) % 1000)) * 100;

    retString += ('' + Math.floor(Math.abs(tmpLng) % 1000)).padStart(2, '0') + "'";
    tmpLng = (((tmpLng / 1000) * 1000) / 1000) * 1000;

    tmpLng = (Math.abs(tmpLng) - Math.floor(Math.abs(tmpLng) % 100)) * 100;

    retString += Math.floor(Math.abs(tmpLng) % 100);

    tmpLng = (Math.abs(tmpLng) - Math.floor(Math.abs(tmpLng) % 100)) * 100;

    tmpLng = Math.round((((tmpLng / 100) * 100) / 100) * 10);

    retString += '.' + tmpLng + '"';

    if (lng > 0) {
      retString += 'E ';
    } else if (lng < 0) {
      retString += 'W ';
    } else {
      retString += ' ';
    }

    return retString;
  }

  formatDate(date: string): string {
    return moment(date).format('Do MMMM YYYY, h:mma');
  }

  /**
   * Populate map features options from API response.
   *
   * Calculate and set map features options based on the provided layers from the API.
   * Also updates the default selected map features in the filter service.
   *
   * Builds default options first, then appends API layers.
   *
   * Preserves domain information on the option so downstream components can detect Wai layers.
   *
   * Merges with any existing user/persisted selections to avoid losing them when options arrive late.
   * Ensures any currently selected keys that are missing from options are retained by adding placeholder options.
   * Computes a merged selection that keeps current selection order, then appends defaults not present.
   *
   * Adds a placeholder option for any currently selected keys that are missing from the options,
   * so that the selection is visible in the UI even if the label is unknown.
   *
   * @access private
   * @param layers Array of MapLayersApi objects fetched from the API
   */
  private populateMapFeaturesOptionsFromApi(layers: MapLayersApi[]): void {
    // Calculate the new ones (skip falsy layers)
    const newMapFeaturesOptions: FilterDrawerReorderableLayoutOptions<any> = [];
    const alwaysFirst = /^(rohe|boundar|observations)/i;
    this.defaultMapFeaturesOptions.forEach((option) => newMapFeaturesOptions.push({ ...option }));
    const safeLayers = (layers || []).filter((l) => l && l.id != null && l.layer_name);
    safeLayers.forEach((layer) =>
      newMapFeaturesOptions.push({
        key: `map-layer-${layer.id}`,
        value: layer.layer_name,
        siteName: layer.site_name,
        domain: layer.domain,
        minZIndex: layer.min_zindex,
        maxZIndex: layer.max_zindex,
        defaultZIndex: layer.default_zindex,
        allowReordering: layer.allow_reordering,
      })
    );

    // Sort and set
    newMapFeaturesOptions.sort((a, b) => (alwaysFirst.test(a.value) ? -1 : a.value > b.value ? 1 : -1));

    this.mapFeaturesOptions = this.compileOptions<string>('mapFeatures', newMapFeaturesOptions);

    const currentFilters = this.filterService.getFilters();
    let currentSelection: string[] = [];
    if (currentFilters && currentFilters.mapFeatures) {
      if (Array.isArray(currentFilters.mapFeatures)) currentSelection = currentFilters.mapFeatures.slice();
      else if (typeof currentFilters.mapFeatures === 'object' && currentFilters.mapFeatures !== null)
        currentSelection = Object.entries(currentFilters.mapFeatures)
          .filter(([k, v]) => !!v)
          .map(([k]) => k);
    }

    const optionKeys = new Set(this.mapFeaturesOptions.map((o) => o.key));
    currentSelection.forEach((sel) => {
      if (!optionKeys.has(sel)) {
        this.mapFeaturesOptions.push({ key: sel, value: sel });
        optionKeys.add(sel);
      }
    });
    const mergedSelection = Array.from(new Set([...currentSelection, ...this.filterService.defaultMapFilters]));

    this.filterService.setFilters({ mapFeatures: mergedSelection });

    // Notify subclasses that the available options have changed. The setFilters
    // call above may be a no-op (selection unchanged), so subclasses cannot rely
    // on the FilterService subscription to recompute derived display state.
    this.onMapFeaturesOptionsUpdated();
  }

  /**
   * Populate map groups options from API response.
   * Calculate and set map groups options based on the provided environmental data sources from the API.
   */
  private populateMapGroupsOptionsFromApi(environmentalDataSources: EnvironmentalDataSource[]): void {
    const mapGroups = extractUniqueGroupTags(environmentalDataSources);

    const optionKeys = new Set(this.mapGroupsOptions.map((o) => o.key));
    mapGroups.forEach((mg) => {
      if (!optionKeys.has(mg)) {
        this.mapGroupsOptions.push({ key: mg, value: mg });
        optionKeys.add(mg);
      }
    });
  }

  private populateContributingOrganisationsOptionsFromApi(organisations: ContributingOrganisation[]): void {
    const transformedOrganisations = (organisations || [])
      .map((organisation) => ({
        key: String(organisation.id),
        value: organisation.name,
      }))
      .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));

    this.contributingOrganisationsOptions = this.compileOptions<string>('contributingOrganisationsFilter', [
      { key: 'all', value: 'All' },
      ...transformedOrganisations,
    ]);
  }

  protected refreshContributingProjectOptions(organisationIds: string[] = []): void {
    this.sharedObservationProjectsSubscription?.unsubscribe();

    const selectedOrganisationIds = (organisationIds || []).filter((id) => id !== 'all');
    this.sharedObservationProjectsSubscription = this.sharedObservationFiltersService
      .getContributingProjects(selectedOrganisationIds)
      .subscribe({
        next: (projects) => {
          this.populateContributingProjectOptionsFromApi(projects || []);
          this.pruneSelectedContributingProjects();
          this.initFilterForm();
        },
        error: (err) => {
          console.error('Error fetching contributing projects', err);
        },
      });
  }

  private populateContributingProjectOptionsFromApi(projects: ContributingProject[]): void {
    const listItems = (projects || [])
      .filter((project) => project.source === 'list_item' && project.id !== null)
      .map((project) => ({
        key: String(project.id),
        value: `${project.name} (${project.origin_organisation_name})`,
      }))
      .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));

    this.contributingProjectListItemOptions = this.compileOptions<string>('contributingProjectsFilter', [
      { key: 'all', value: 'All' },
      ...listItems,
    ]);
  }

  private pruneSelectedContributingProjects(): void {
    const listItemKeys = this.contributingProjectListItemOptions.map((option) => option.key);

    const selectedListItems = this.selectedContributingProjectListItems.filter((id) => listItemKeys.includes(id));

    const nextListItems = selectedListItems.length ? selectedListItems : ['all'];

    this.selectedContributingProjectListItems = nextListItems;
    this.filterService.setFilters({
      selectedContributingProjectListItems: nextListItems,
    });
  }

  /**
   * Return currently selected map-layer keys
   *
   * (e.g. 'map-layer-123')
   *
   * Handles cases where the form control value is either an array of
   * strings (legacy or initial values)
   *
   * @returns {string[]} Array of selected map-layer keys
   */
  getSelectedMapLayerKeys(): string[] {
    const mv = this.filterForm?.value?.mapFeatures;
    if (!mv) return [];

    if (Array.isArray(mv)) {
      const keys = mv.filter((f: string) => /^map-layer-\d+/.test(f));
      console.debug('[map-order] getSelectedMapLayerKeys (array) ->', keys);
      return keys;
    }

    if (typeof mv === 'object') {
      const keys = Object.entries(mv)
        .filter(([_key, val]) => !!val)
        .map(([key]) => key)
        .filter((k) => /^map-layer-\d+/.test(k));
      console.debug('[map-order] getSelectedMapLayerKeys (object) ->', keys);
      return keys;
    }

    return [];
  }

  /**
   * Return array of option objects for selected map-layer keys with their labels.
   */
  getSelectedMapLayerOptions(): { key: string; label: string }[] {
    const keys = this.getSelectedMapLayerKeys();
    return keys
      .map((k: string) => ({ key: k, label: this.mapFeaturesOptions.find((o) => o.key === k)?.value || k }))
      .filter(Boolean);
  }

  /**
   * Persist a custom order for map layers for the current site in localStorage.
   * Order is stored as array of layer ids (strings like '123').
   *
   * If no site is selected, or an error occurs, nothing is saved.
   *
   * @param {string[]} orderKeys Array of map-layer keys in desired order (e.g. 'map-layer-123')
   */
  saveCustomMapLayerOrder(orderKeys: string[]): void {
    try {
      this.mapService.saveCustomMapLayerOrderForSite(this.site?.id ?? null, orderKeys);
    } catch (e) {
      console.error('Error delegating saveCustomMapLayerOrder to MapService', e);
    }
  }

  /**
   * Load a previously saved custom order (returns array of keys like 'map-layer-123')
   */
  loadCustomMapLayerOrder(): string[] | null {
    try {
      return this.mapService.loadCustomMapLayerOrderForSite(this.site?.id ?? null);
    } catch (e) {
      console.error('Error delegating loadCustomMapLayerOrder to MapService', e);
      return null;
    }
  }

  /**
   * Reset custom order for current site.
   */
  resetCustomMapLayerOrder(): void {
    try {
      this.mapService.saveCustomMapLayerOrderForSite(this.site?.id ?? null, []);
    } catch (e) {
      console.error('Error delegating resetCustomMapLayerOrder to MapService', e);
    }
  }

  /**
   * Persist the currently selected map layer keys for the current site to localStorage.
   * Stores as an array of numeric ids (without the 'map-layer-' prefix) at key `mapLayerSelection:{siteId}`.
   */
  protected saveMapLayerSelectionToLocalStorage(keys: string[]): void {
    try {
      this.mapService.saveMapLayerSelectionForSite(this.site?.id ?? null, keys);
    } catch (e) {
      console.error('Error delegating saveMapLayerSelectionToLocalStorage to MapService', e);
    }
  }

  protected saveMapGroupSelectionToLocalStorage(keys: string[]): void {
    try {
      this.mapService.saveMapGroupSelectionToLocalStorage(this.site?.id ?? null, keys);
    } catch (e) {
      console.error('Error delegating saveMapLayerSelectionToLocalStorage to MapService', e);
    }
  }

  /**
   * Load previously saved map layer selection for the current site from localStorage.
   * Returns an array of keys like 'map-layer-123' or null if none.
   */
  protected loadMapLayerSelectionFromLocalStorage(): string[] | null {
    try {
      return this.mapService.loadMapLayerSelectionForSite(this.site?.id ?? null);
    } catch (e) {
      console.error('Error delegating loadMapLayerSelectionFromLocalStorage to MapService', e);
      return null;
    }
  }

  /**
   * Load previously saved map layer selection for the given site from localStorage.
   * Returns an array of keys like 'Baseline Monitoring' or null if none.
   */
  protected loadMapGroupSelectionFromLocalStorage(): string[] | null {
    try {
      return this.mapService.loadMapGroupSelectionFromLocalStorage(this.site?.id ?? null);
    } catch (e) {
      console.error('Error delegating loadMapGroupSelectionFromLocalStorage to MapService', e);
      return null;
    }
  }

  private getCheckboxGroupValues = (field: string) =>
    Object.entries(this.filterForm.value[field]).reduce((accumulator, [key, val]) => {
      if (val) {
        accumulator.push(key);
      }

      return accumulator;
    }, [] as string[]);

  /**
   * Initialize the filter form with current filter values from the FilterService.
   * Sets up a subscription to form value changes to update the FilterService accordingly.
   * Also handles local persistence of mapFeatures changes via a hook method.
   *
   * If the form's type control value differs from the component's type property,
   * it is updated to ensure consistency.
   *
   * Uses auditTime(0) to debounce rapid changes, particularly from date range inputs.
   *
   * Subclasses can override onLocalMapFeaturesChange to react to local changes.
   * Can set programmatically without emitting valueChanges to avoid triggering during init.
   *
   * Ensures only one subscription to valueChanges is active by unsubscribing any existing one first.
   * Ensures proper cleanup by storing the subscription in a class property.
   * Re-initializing the form will reset the subscription safely.
   *
   * Excludes any fields declared as manual-apply from automatic updates.
   *
   * @access private
   */
  private initFilterForm(): void {
    const currentFilters = this.filterService.getFilters();

    const filterGroup = {
      type: this.type,
      domain: currentFilters.domain,
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      searchTerm: currentFilters.searchTerm,
      sensor: currentFilters.sensor,
      mapFeatures: currentFilters.mapFeatures,
      baseMap: currentFilters.baseMap,
      dataLayer: currentFilters.dataLayer,
      matauranga: currentFilters.matauranga,
      monitoring: currentFilters.monitoring,
      observation: currentFilters.observation,
      calendarType: currentFilters.calendarType,
      calendarTypeYear: currentFilters.calendarTypeYear,
      calendarTypeMonth: currentFilters.calendarTypeMonth,
      calendarTypeWeek: currentFilters.calendarTypeWeek,
      calendarTypeDay: currentFilters.calendarTypeDay,
      selectedLocation: currentFilters.selectedLocation,
      selectedContributingOrganisations: currentFilters.selectedContributingOrganisations,
      selectedContributingProjectListItems: currentFilters.selectedContributingProjectListItems,
      ...this.getCheckboxFields().reduce(
        (accumulator, field: string) => ({
          ...accumulator,
          [field]: this.buildCheckboxGroup(
            this[`${field}Options` as keyof FilterDrawerLayoutComponentBase],
            currentFilters[field]
          ),
        }),
        {} as any
      ),
      ...this.getMultipleSelectFields().reduce(
        (accumulator, field: string) => ({ ...accumulator, [field]: [currentFilters[field]] }),
        {} as any
      ),
    };

    this.filterForm = this.fb.group(filterGroup);

    if (this.filterForm.controls.type.value !== this.type) {
      this.filterForm.controls.type.setValue(this.type, { emitEvent: false });
    }

    /**
     * Need auditTime to 'debounce' and get the last event emitted as the material date range emits
     * multiple events
     */
    this.valueChangesSub?.unsubscribe();

    this.valueChangesSub = this.filterForm.valueChanges
      .pipe(
        auditTime(0),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe({
        next: () => {
          const { type, ...filterValues } = this.filterForm.value;

          this.contentClass = { [type]: true };

          try {
            const mapFeaturesValue = (filterValues as any).mapFeatures ?? null;
            let normalized: string[] | null = null;
            if (Array.isArray(mapFeaturesValue)) {
              normalized = mapFeaturesValue.filter((f: any) => typeof f === 'string');
            } else if (typeof mapFeaturesValue === 'object' && mapFeaturesValue !== null) {
              normalized = Object.entries(mapFeaturesValue)
                .filter(([_k, v]) => !!v)
                .map(([k]) => k);
            }
            this.onLocalMapFeaturesChange(normalized);
          } catch (e) {
            console.debug('Error in onLocalMapFeaturesChange hook', e);
          }

          const manual = Array.isArray(this.getManualApplyFields()) ? this.getManualApplyFields() : [];
          const toApply: any = {};
          Object.keys(filterValues).forEach((k) => {
            if (!manual.includes(k)) {
              toApply[k] = (filterValues as any)[k];
            }
          });

          this.filterService.setFilters({
            ...toApply,
            ...this.getCheckboxFields().reduce(
              (accumulator, field: string) => ({ ...accumulator, [field]: this.getCheckboxGroupValues(field) }),
              {} as any
            ),
          });
        },
      });

    const hook = (this as any).onFilterFormInitialized;
    if (typeof hook === 'function') {
      hook.call(this);
    }
  }
}
